import { useState } from 'react'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useTicketCompanies } from '@/queries/tickets'
import { useSaveLearning } from '@/queries/learnings'
import { CHECK_TOOLS, FIX_TOOLS, versionedFields, type Learning, type LearningFields } from '@/types/learning'

const FIELD = 'w-full rounded-etn-card border border-etn-border-input bg-etn-surface px-3 py-2 text-etn-control'
const LABEL = 'mb-1 block text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// Arguments are edited as JSON text and only parsed on save, so a half-typed object is not lost
type Row<T> = Omit<T, 'args'> & { argsText: string }

function toRows<T extends { args: Record<string, unknown> }>(items: T[]): Row<T>[] {
  return items.map(({ args, ...rest }) => ({ ...(rest as Omit<T, 'args'>), argsText: JSON.stringify(args ?? {}, null, 2) }))
}

function parseArgs(text: string, label: string): Record<string, unknown> {
  let value: unknown
  try {
    value = JSON.parse(text || '{}')
  } catch {
    throw new Error(`${label}: the arguments are not valid JSON`)
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label}: the arguments must be a JSON object`)
  return value as Record<string, unknown>
}

export interface LearningEditorProps {
  initial: LearningFields
  learning?: Learning
  sourceInvestigationId?: string
  onSaved?: (learning: Learning) => void
  onCancel?: () => void
}

/**
 * Create or edit a learning. Known fixes carry the symptoms, the reads that must confirm them, the
 * changes to propose and a reply; client context is a titled note about one company.
 */
export function LearningEditor({ initial, learning, sourceInvestigationId, onSaved, onCancel }: LearningEditorProps) {
  const save = useSaveLearning(learning?._id)
  const { data: companies } = useTicketCompanies()
  const [fields, setFields] = useState(() => versionedFields(initial))
  const [checks, setChecks] = useState(() => toRows(initial.confirmationChecks))
  const [actions, setActions] = useState(() => toRows(initial.fixActions))
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof LearningFields>(key: K, value: LearningFields[K]) => setFields(f => ({ ...f, [key]: value }))
  const knownFix = fields.kind === 'known_fix'
  const scope = knownFix ? fields.scope : 'company'

  const submit = () => {
    setError(null)
    let body: LearningFields
    try {
      body = {
        ...fields,
        scope,
        companyId: scope === 'company' ? fields.companyId : null,
        confirmationChecks: knownFix ? checks.map(({ argsText, ...c }, i) => ({ ...c, args: parseArgs(argsText, `Check ${i + 1}`) })) : [],
        fixActions: knownFix ? actions.map(({ argsText, ...a }, i) => ({ ...a, args: parseArgs(argsText, `Fix action ${i + 1}`) })) : []
      }
    } catch (e) {
      setError((e as Error).message)
      return
    }
    save.mutate(
      { ...body, ...(sourceInvestigationId ? { sourceInvestigationId } : {}), ...(learning ? { expectedVersion: learning.version } : {}) },
      {
        onSuccess: saved => onSaved?.(saved),
        onError: err => setError(extractErrorMessage(err, 'Could not save the learning'))
      }
    )
  }

  return (
    <form
      className="space-y-4"
      aria-label="Learning"
      onSubmit={e => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="block">
          <span className={LABEL}>Kind</span>
          <select
            className={FIELD}
            value={fields.kind}
            disabled={Boolean(learning)}
            onChange={e => set('kind', e.target.value as LearningFields['kind'])}
          >
            <option value="known_fix">Known fix</option>
            <option value="client_context">Client context</option>
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Scope</span>
          <select className={FIELD} value={scope} disabled={!knownFix} onChange={e => set('scope', e.target.value as LearningFields['scope'])}>
            <option value="global">Every company</option>
            <option value="company">One company</option>
          </select>
        </label>
        {scope === 'company' && (
          <label className="block">
            <span className={LABEL}>Company</span>
            <select className={FIELD} value={fields.companyId ?? ''} onChange={e => set('companyId', e.target.value || null)}>
              <option value="">Choose a company</option>
              {fields.companyId && !companies?.data.some(c => c._id === fields.companyId) && (
                <option value={fields.companyId}>{fields.companyId}</option>
              )}
              {(companies?.data ?? []).map(c => (
                <option key={c._id} value={c._id}>
                  {c.name ?? c._id}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label className="block">
        <span className={LABEL}>Title</span>
        <input className={FIELD} value={fields.title} maxLength={200} onChange={e => set('title', e.target.value)} />
      </label>

      {knownFix ? (
        <>
          <label className="block">
            <span className={LABEL}>Symptoms</span>
            <textarea className={FIELD} rows={3} value={fields.symptoms} onChange={e => set('symptoms', e.target.value)} />
          </label>

          <fieldset className="space-y-2">
            <legend className={LABEL}>Confirmation checks</legend>
            <p className="text-xs text-etn-text-soft">
              Reads that must all show what you expect before the Support Agent may answer a ticket with this fix. Arguments may use{' '}
              <code>{'{{companyId}}'}</code>, <code>{'{{reporterId}}'}</code>, <code>{'{{reporterEmail}}'}</code> or{' '}
              <code>{'{{ticketSubject}}'}</code>.
            </p>
            {checks.map((check, i) => (
              <div
                key={i}
                className="grid grid-cols-1 gap-2 rounded-etn-card border border-etn-border-hairline p-3 md:grid-cols-[200px_minmax(0,1fr)_auto]"
                data-testid="check-row"
              >
                <select
                  aria-label={`Check ${i + 1} tool`}
                  className={FIELD}
                  value={check.tool}
                  onChange={e => setChecks(cs => cs.map((c, j) => (j === i ? { ...c, tool: e.target.value } : c)))}
                >
                  {CHECK_TOOLS.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="space-y-2">
                  <textarea
                    aria-label={`Check ${i + 1} arguments`}
                    className={`${FIELD} font-mono text-xs`}
                    rows={3}
                    value={check.argsText}
                    onChange={e => setChecks(cs => cs.map((c, j) => (j === i ? { ...c, argsText: e.target.value } : c)))}
                  />
                  <input
                    aria-label={`Check ${i + 1} expectation`}
                    className={FIELD}
                    placeholder="What the read must show"
                    value={check.expectation}
                    onChange={e => setChecks(cs => cs.map((c, j) => (j === i ? { ...c, expectation: e.target.value } : c)))}
                  />
                </div>
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label={`Remove check ${i + 1}`}
                  onClick={() => setChecks(cs => cs.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => setChecks(cs => [...cs, { tool: CHECK_TOOLS[0], argsText: '{}', expectation: '' }])}
            >
              <Plus />
              Add check
            </Button>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className={LABEL}>Fix actions</legend>
            <p className="text-xs text-etn-text-soft">Proposed for staff to apply, never applied by the Support Agent.</p>
            {actions.map((action, i) => (
              <div
                key={i}
                className="grid grid-cols-1 gap-2 rounded-etn-card border border-etn-border-hairline p-3 md:grid-cols-[200px_minmax(0,1fr)_auto]"
                data-testid="fix-row"
              >
                <select
                  aria-label={`Fix action ${i + 1} tool`}
                  className={FIELD}
                  value={action.tool}
                  onChange={e => setActions(as => as.map((a, j) => (j === i ? { ...a, tool: e.target.value as typeof a.tool } : a)))}
                >
                  {FIX_TOOLS.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="space-y-2">
                  <textarea
                    aria-label={`Fix action ${i + 1} arguments`}
                    className={`${FIELD} font-mono text-xs`}
                    rows={3}
                    value={action.argsText}
                    onChange={e => setActions(as => as.map((a, j) => (j === i ? { ...a, argsText: e.target.value } : a)))}
                  />
                  <input
                    aria-label={`Fix action ${i + 1} summary`}
                    className={FIELD}
                    placeholder="One line staff approve"
                    value={action.summary}
                    onChange={e => setActions(as => as.map((a, j) => (j === i ? { ...a, summary: e.target.value } : a)))}
                  />
                </div>
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label={`Remove fix action ${i + 1}`}
                  onClick={() => setActions(as => as.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => setActions(as => [...as, { tool: FIX_TOOLS[0], argsText: '{}', summary: '' }])}
            >
              <Plus />
              Add fix action
            </Button>
          </fieldset>

          <label className="block">
            <span className={LABEL}>Reply template (optional)</span>
            <textarea className={FIELD} rows={4} value={fields.replyTemplate} onChange={e => set('replyTemplate', e.target.value)} />
          </label>
        </>
      ) : (
        <label className="block">
          <span className={LABEL}>What the Support Agent should know (markdown)</span>
          <textarea className={FIELD} rows={8} maxLength={8000} value={fields.body} onChange={e => set('body', e.target.value)} />
        </label>
      )}

      <label className="block">
        <span className={LABEL}>Notes for staff</span>
        <textarea className={FIELD} rows={2} value={fields.notes} onChange={e => set('notes', e.target.value)} />
      </label>

      {error && (
        <p className="text-etn-control text-etn-danger-text" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending && <Loader2 className="animate-spin" />}
          {learning ? 'Save new version' : 'Save learning'}
        </Button>
        {onCancel && (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}
